import type { Activity, ScheduleModel } from '../types/schedule';
import { findProjectFinishActivity } from './analytics';

/**
 * DCMA 14-Point Schedule Assessment.
 *
 * Two rules run through all of it:
 *  - Every time-relative test keys off the schedule's **data date**, never the
 *    wall clock. A schedule assessed six months after export must produce the
 *    same numbers it produced on the day it was issued.
 *  - Checks are scoped to **incomplete** activities, per the DCMA guidance.
 *    Completed work cannot be replanned, so counting it distorts every ratio.
 *
 * Checks 11 and 14 require a baseline and report as not-applicable without
 * one, rather than silently substituting a proxy.
 */

export type CheckStatus = 'pass' | 'warning' | 'fail' | 'info' | 'na';

export interface DCMACheck {
  /** DCMA point number, 1–14. */
  number: number;
  key: string;
  name: string;
  /** Formatted for display. */
  value: string;
  /** Comparable magnitude, used for the scorecard bars. Null when N/A. */
  numeric: number | null;
  /** Share of the assessed population, 0–1, where the check is a proportion. */
  ratio: number | null;
  threshold: string;
  status: CheckStatus;
  description: string;
  /** What the check is looking for, in plain language. */
  guidance: string;
  /** Activities that failed, for drill-through. */
  failingActivityIds: string[];
  /** Population the check was assessed against. */
  population: number;
}

export interface DCMAResult {
  checks: DCMACheck[];
  /** Checks passed out of those that could be assessed. */
  passed: number;
  assessable: number;
  score: number;
  incompleteCount: number;
  dataDate: Date | null;
  hasBaseline: boolean;
  generatedAt: string;
}

const HIGH_FLOAT_DAYS = 44;
const HIGH_DURATION_DAYS = 44;
const FIVE_PERCENT = 0.05;

/** Working days between two dates on a five-day week. */
function workingDaysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  if (ms <= 0) return 0;
  const totalDays = ms / 86_400_000;
  return Math.round((totalDays / 7) * 5);
}

function ratioStatus(ratio: number, warnAt = FIVE_PERCENT, failAt = 0.1): CheckStatus {
  if (ratio <= warnAt) return 'pass';
  return ratio <= failAt ? 'warning' : 'fail';
}

function pct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export class DCMAAnalyzer {
  private readonly schedule: ScheduleModel;
  private readonly baseline?: ScheduleModel;
  private readonly incomplete: Activity[];
  private readonly dataDate: Date | null;

  constructor(schedule: ScheduleModel, baseline?: ScheduleModel) {
    this.schedule = schedule;
    this.baseline = baseline;
    this.dataDate = schedule.dataDate;

    // Level-of-effort and WBS-summary rows are containers, not work, and
    // DCMA excludes them from the activity-level checks.
    this.incomplete = schedule.activities.filter(
      (a) =>
        !a.isComplete &&
        a.type !== 'Level of Effort' &&
        a.type !== 'WBS Summary'
    );
  }

  /** Check 1 — Logic. Incomplete activities missing a predecessor or successor. */
  private logic(): DCMACheck {
    // The first and last activities in the network legitimately have one open
    // end; excluding them prevents two guaranteed false positives.
    const openStarts = this.incomplete.filter((a) => a.predecessors.length === 0);
    const openEnds = this.incomplete.filter((a) => a.successors.length === 0);
    const projectStart = openStarts.length === 1 ? openStarts[0].id : null;
    const projectFinish = openEnds.length === 1 ? openEnds[0].id : null;

    const failing = this.incomplete.filter((a) => {
      if (a.id === projectStart && a.successors.length > 0) return false;
      if (a.id === projectFinish && a.predecessors.length > 0) return false;
      return a.predecessors.length === 0 || a.successors.length === 0;
    });

    const ratio = this.incomplete.length ? failing.length / this.incomplete.length : 0;
    return {
      number: 1,
      key: 'logic',
      name: 'Logic',
      value: `${failing.length} (${pct(ratio)})`,
      numeric: failing.length,
      ratio,
      threshold: '≤ 5%',
      status: ratioStatus(ratio),
      description: `${failing.length} of ${this.incomplete.length} incomplete activities are missing a predecessor or a successor.`,
      guidance:
        'Every activity should be driven by something and drive something else. Open ends mean the activity cannot move when the work around it moves.',
      failingActivityIds: failing.map((a) => a.id),
      population: this.incomplete.length,
    };
  }

  /** Check 2 — Leads. Negative lag on any relationship. */
  private leads(): DCMACheck {
    const failing = this.schedule.relationships.filter((r) => r.lagHours < 0);
    const total = this.schedule.relationships.length;
    const ratio = total ? failing.length / total : 0;

    return {
      number: 2,
      key: 'leads',
      name: 'Leads',
      value: String(failing.length),
      numeric: failing.length,
      ratio,
      threshold: '0',
      status: failing.length === 0 ? 'pass' : 'fail',
      description: `${failing.length} of ${total} relationships carry negative lag.`,
      guidance:
        'Negative lag pulls a successor back before its predecessor finishes, which distorts the critical path and hides true duration.',
      failingActivityIds: failing.map((r) => r.successorId),
      population: total,
    };
  }

  /** Check 3 — Lags. Positive lag on more than 5% of relationships. */
  private lags(): DCMACheck {
    const failing = this.schedule.relationships.filter((r) => r.lagHours > 0);
    const total = this.schedule.relationships.length;
    const ratio = total ? failing.length / total : 0;

    return {
      number: 3,
      key: 'lags',
      name: 'Lags',
      value: `${failing.length} (${pct(ratio)})`,
      numeric: failing.length,
      ratio,
      threshold: '≤ 5%',
      status: ratioStatus(ratio),
      description: `${failing.length} of ${total} relationships carry positive lag.`,
      guidance:
        'Lag is uncosted, unresourced, invisible time. Heavy use usually means missing activities that should be modelled explicitly.',
      failingActivityIds: failing.map((r) => r.successorId),
      population: total,
    };
  }

  /** Check 4 — Relationship types. Finish-to-Start should be at least 90%. */
  private relationshipTypes(): DCMACheck {
    const total = this.schedule.relationships.length;
    const counts = { FS: 0, SS: 0, FF: 0, SF: 0 };
    for (const rel of this.schedule.relationships) counts[rel.type]++;

    const fsRatio = total ? counts.FS / total : 0;
    const nonFs = this.schedule.relationships.filter((r) => r.type !== 'FS');

    return {
      number: 4,
      key: 'relationshipTypes',
      name: 'Relationship Types',
      value: `FS ${pct(fsRatio)}`,
      numeric: counts.FS,
      ratio: fsRatio,
      threshold: 'FS ≥ 90%',
      status: fsRatio >= 0.9 ? 'pass' : fsRatio >= 0.8 ? 'warning' : 'fail',
      description: `FS ${counts.FS}, SS ${counts.SS}, FF ${counts.FF}, SF ${counts.SF} across ${total} relationships.`,
      guidance:
        'Finish-to-Start is the only link type that models work handing over cleanly. Heavy SS/FF use overlaps work in ways that rarely survive contact with site.',
      failingActivityIds: nonFs.map((r) => r.successorId),
      population: total,
    };
  }

  /** Check 5 — Hard constraints on incomplete activities. */
  private hardConstraints(): DCMACheck {
    const failing = this.incomplete.filter((a) => a.hasHardConstraint);
    const ratio = this.incomplete.length ? failing.length / this.incomplete.length : 0;

    return {
      number: 5,
      key: 'hardConstraints',
      name: 'Hard Constraints',
      value: `${failing.length} (${pct(ratio)})`,
      numeric: failing.length,
      ratio,
      threshold: '≤ 5%',
      status: ratioStatus(ratio),
      description: `${failing.length} of ${this.incomplete.length} incomplete activities carry a Mandatory Start/Finish or Start On/Finish On constraint.`,
      guidance:
        'Hard constraints override logic. An activity pinned to a date will not move when its predecessors slip, so the schedule stops telling you the truth.',
      failingActivityIds: failing.map((a) => a.id),
      population: this.incomplete.length,
    };
  }

  /** Check 6 — High float, above 44 working days. */
  private highFloat(): DCMACheck {
    const failing = this.incomplete.filter((a) => a.totalFloatDays > HIGH_FLOAT_DAYS);
    const ratio = this.incomplete.length ? failing.length / this.incomplete.length : 0;

    return {
      number: 6,
      key: 'highFloat',
      name: 'High Float',
      value: `${failing.length} (${pct(ratio)})`,
      numeric: failing.length,
      ratio,
      threshold: '≤ 5%',
      status: ratioStatus(ratio),
      description: `${failing.length} of ${this.incomplete.length} incomplete activities have total float above ${HIGH_FLOAT_DAYS} days.`,
      guidance:
        'Float over two months usually signals missing successor logic rather than genuine slack.',
      failingActivityIds: failing.map((a) => a.id),
      population: this.incomplete.length,
    };
  }

  /** Check 7 — Negative float. */
  private negativeFloat(): DCMACheck {
    const failing = this.incomplete.filter((a) => a.totalFloatDays < 0);
    const ratio = this.incomplete.length ? failing.length / this.incomplete.length : 0;
    const worst = failing.reduce(
      (min, a) => Math.min(min, a.totalFloatDays),
      0
    );

    return {
      number: 7,
      key: 'negativeFloat',
      name: 'Negative Float',
      value: String(failing.length),
      numeric: failing.length,
      ratio,
      threshold: '0',
      status: failing.length === 0 ? 'pass' : 'fail',
      description: failing.length
        ? `${failing.length} activities carry negative float, the worst at ${worst.toFixed(0)} days.`
        : 'No activities carry negative float.',
      guidance:
        'Negative float means the schedule cannot meet a date it has been told to meet. It needs either a recovery plan or a re-baseline.',
      failingActivityIds: failing.map((a) => a.id),
      population: this.incomplete.length,
    };
  }

  /** Check 8 — High duration, above 44 working days remaining. */
  private highDuration(): DCMACheck {
    const failing = this.incomplete.filter(
      (a) => !a.isMilestone && a.remainingDurationDays > HIGH_DURATION_DAYS
    );
    const population = this.incomplete.filter((a) => !a.isMilestone).length;
    const ratio = population ? failing.length / population : 0;

    return {
      number: 8,
      key: 'highDuration',
      name: 'High Duration',
      value: `${failing.length} (${pct(ratio)})`,
      numeric: failing.length,
      ratio,
      threshold: '≤ 5%',
      status: ratioStatus(ratio),
      description: `${failing.length} of ${population} incomplete activities have more than ${HIGH_DURATION_DAYS} days remaining.`,
      guidance:
        'Long activities hide progress. Anything over two months should usually be broken down so status means something.',
      failingActivityIds: failing.map((a) => a.id),
      population,
    };
  }

  /** Check 9 — Invalid dates relative to the data date. */
  private invalidDates(): DCMACheck {
    if (!this.dataDate) {
      return {
        number: 9,
        key: 'invalidDates',
        name: 'Invalid Dates',
        value: 'N/A',
        numeric: null,
        ratio: null,
        threshold: '0',
        status: 'na',
        description: 'No data date available, so date validity cannot be assessed.',
        guidance:
          'Actual dates must sit on or before the data date; forecast dates must sit after it.',
        failingActivityIds: [],
        population: 0,
      };
    }

    const dd = this.dataDate;
    const failing = this.schedule.activities.filter((a) => {
      // Actuals in the future.
      if (a.actualStart && a.actualStart > dd) return true;
      if (a.actualFinish && a.actualFinish > dd) return true;
      // Forecast work in the past.
      if (!a.isComplete && a.targetFinish && a.targetFinish < dd && !a.actualFinish) {
        return true;
      }
      return false;
    });

    const population = this.schedule.activities.length;
    return {
      number: 9,
      key: 'invalidDates',
      name: 'Invalid Dates',
      value: String(failing.length),
      numeric: failing.length,
      ratio: population ? failing.length / population : 0,
      threshold: '0',
      status: failing.length === 0 ? 'pass' : 'fail',
      description: `${failing.length} activities have an actual date after the data date, or remaining work forecast before it.`,
      guidance:
        'Work cannot have happened in the future, and remaining work cannot be scheduled in the past. Either indicates the schedule was not properly progressed.',
      failingActivityIds: failing.map((a) => a.id),
      population,
    };
  }

  /** Check 10 — Resource and cost loading on incomplete activities. */
  private resources(): DCMACheck {
    const population = this.incomplete.filter((a) => !a.isMilestone).length;
    if (population === 0) {
      return {
        number: 10,
        key: 'resources',
        name: 'Resources',
        value: 'N/A',
        numeric: null,
        ratio: null,
        threshold: '100% loaded',
        status: 'na',
        description: 'No incomplete non-milestone activities to assess.',
        guidance: 'Every remaining activity should carry the resource or cost needed to do it.',
        failingActivityIds: [],
        population: 0,
      };
    }

    const failing = this.incomplete.filter(
      (a) => !a.isMilestone && a.resources.length === 0
    );
    const loadedRatio = (population - failing.length) / population;

    return {
      number: 10,
      key: 'resources',
      name: 'Resources',
      value: pct(loadedRatio),
      numeric: population - failing.length,
      ratio: loadedRatio,
      threshold: '100% loaded',
      status: loadedRatio >= 0.95 ? 'pass' : loadedRatio >= 0.85 ? 'warning' : 'fail',
      description: `${population - failing.length} of ${population} incomplete activities carry a resource or cost assignment.`,
      guidance:
        'An unresourced schedule cannot tell you whether the plan is achievable, only whether the dates line up.',
      failingActivityIds: failing.map((a) => a.id),
      population,
    };
  }

  /** Check 11 — Missed tasks against baseline finish. */
  private missedTasks(): DCMACheck {
    const base: Omit<DCMACheck, 'value' | 'numeric' | 'ratio' | 'status' | 'description' | 'failingActivityIds' | 'population'> = {
      number: 11,
      key: 'missedTasks',
      name: 'Missed Tasks',
      threshold: '≤ 5%',
      guidance:
        'Counts activities that should have finished by now according to the baseline but did not. This is the clearest single read on whether the plan is holding.',
    };

    if (!this.baseline || !this.dataDate) {
      return {
        ...base,
        value: 'N/A',
        numeric: null,
        ratio: null,
        status: 'na',
        description: this.baseline
          ? 'No data date available.'
          : 'Upload a baseline schedule to assess missed tasks.',
        failingActivityIds: [],
        population: 0,
      };
    }

    const dd = this.dataDate;
    const baselineByCode = new Map(
      this.baseline.activities.map((a) => [a.code, a])
    );

    const dueByNow = this.schedule.activities.filter((a) => {
      const baselineActivity = baselineByCode.get(a.code);
      return !!baselineActivity?.finish && baselineActivity.finish <= dd;
    });

    const failing = dueByNow.filter((a) => {
      const baselineActivity = baselineByCode.get(a.code)!;
      if (!a.isComplete) return true;
      return !!a.actualFinish && !!baselineActivity.finish && a.actualFinish > baselineActivity.finish;
    });

    const ratio = dueByNow.length ? failing.length / dueByNow.length : 0;
    return {
      ...base,
      value: `${failing.length} (${pct(ratio)})`,
      numeric: failing.length,
      ratio,
      status: ratioStatus(ratio),
      description: `${failing.length} of ${dueByNow.length} activities due by the data date finished late or have not finished.`,
      failingActivityIds: failing.map((a) => a.id),
      population: dueByNow.length,
    };
  }

  /**
   * Check 12 — Critical path test.
   *
   * The formal test perturbs a critical activity by 600 days and confirms the
   * project finish moves with it, which needs a CPM engine. In its place we
   * test the property that test is probing for: that every critical activity
   * sits on an unbroken logic chain reaching the project finish. A critical
   * activity that cannot reach the finish is exactly the broken network the
   * 600-day test is designed to expose.
   */
  private criticalPathTest(): DCMACheck {
    const finishActivity = findProjectFinishActivity(this.schedule);

    const base = {
      number: 12,
      key: 'criticalPathTest',
      name: 'Critical Path Test',
      threshold: 'Unbroken to finish',
      guidance:
        'Every critical activity must have a continuous chain of logic through to project completion, otherwise delay to it will not show up in the finish date.',
    };

    if (!finishActivity) {
      return {
        ...base,
        value: 'N/A',
        numeric: null,
        ratio: null,
        status: 'na',
        description: 'Could not identify a project finish activity.',
        failingActivityIds: [],
        population: 0,
      };
    }

    // Reverse walk from the finish activity across predecessor links.
    const reachesFinish = new Set<string>([finishActivity.id]);
    const queue = [finishActivity.id];
    while (queue.length) {
      const current = this.schedule.activityById.get(queue.pop()!);
      if (!current) continue;
      for (const rel of current.predecessors) {
        if (!reachesFinish.has(rel.predecessorId)) {
          reachesFinish.add(rel.predecessorId);
          queue.push(rel.predecessorId);
        }
      }
    }

    const criticalIncomplete = this.incomplete.filter((a) => a.isCritical);
    const failing = criticalIncomplete.filter((a) => !reachesFinish.has(a.id));
    const ratio = criticalIncomplete.length
      ? failing.length / criticalIncomplete.length
      : 0;

    return {
      ...base,
      value: failing.length === 0 ? 'Passed' : `${failing.length} broken`,
      numeric: failing.length,
      ratio,
      status: criticalIncomplete.length === 0 ? 'na' : failing.length === 0 ? 'pass' : 'fail',
      description:
        criticalIncomplete.length === 0
          ? 'No incomplete critical activities to test.'
          : `${failing.length} of ${criticalIncomplete.length} critical activities have no logic path to the project finish (${finishActivity.code}).`,
      failingActivityIds: failing.map((a) => a.id),
      population: criticalIncomplete.length,
    };
  }

  /** Check 13 — Critical Path Length Index. */
  private cpli(): { check: DCMACheck; criticalPathLength: number } {
    const base = {
      number: 13,
      key: 'cpli',
      name: 'CPLI',
      threshold: '≥ 0.95',
      guidance:
        'Critical Path Length Index: how much float the remaining critical path has against the target finish. Below 1.00 the schedule is already eroding.',
    };

    const finishActivity = findProjectFinishActivity(this.schedule);

    if (!this.dataDate || !finishActivity?.finish) {
      return {
        criticalPathLength: 0,
        check: {
          ...base,
          value: 'N/A',
          numeric: null,
          ratio: null,
          status: 'na',
          description: 'Needs a data date and a project finish date.',
          failingActivityIds: [],
          population: 0,
        },
      };
    }

    // CPL is the remaining span from data date to project finish; the total
    // float on the finishing activity is what is left to give.
    const criticalPathLength = workingDaysBetween(this.dataDate, finishActivity.finish);
    const projectFloat = finishActivity.totalFloatDays;

    if (criticalPathLength <= 0) {
      // There is no remaining path to index. If the completion milestone is
      // still open, that is a finding in its own right rather than a gap in
      // the data — staying silent here would hide the worst case.
      const stillOpen = !finishActivity.isComplete;
      return {
        criticalPathLength: 0,
        check: {
          ...base,
          value: stillOpen ? 'Overdue' : 'Complete',
          numeric: null,
          ratio: null,
          status: stillOpen ? 'fail' : 'na',
          description: stillOpen
            ? `${finishActivity.code} is the project completion activity and its forecast finish (${finishActivity.finish.toLocaleDateString('en-GB')}) is on or before the data date, yet it is not complete. There is no remaining critical path to index.`
            : 'The project completion activity is already complete.',
          failingActivityIds: stillOpen ? [finishActivity.id] : [],
          population: 0,
        },
      };
    }

    const value = (criticalPathLength + projectFloat) / criticalPathLength;
    return {
      criticalPathLength,
      check: {
        ...base,
        value: value.toFixed(2),
        numeric: value,
        ratio: null,
        status: value >= 0.95 ? 'pass' : value >= 0.85 ? 'warning' : 'fail',
        description: `Critical path length ${criticalPathLength} working days from the data date, with ${projectFloat.toFixed(0)} days of float on ${finishActivity.code}.`,
        failingActivityIds: [],
        population: criticalPathLength,
      },
    };
  }

  /** Check 14 — Baseline Execution Index. */
  private bei(): DCMACheck {
    const base = {
      number: 14,
      key: 'bei',
      name: 'BEI',
      threshold: '≥ 0.95',
      guidance:
        'Baseline Execution Index: activities actually finished, divided by those the baseline said would be finished by now. Below 1.00 the team is behind the plan.',
    };

    if (!this.baseline || !this.dataDate) {
      return {
        ...base,
        value: 'N/A',
        numeric: null,
        ratio: null,
        status: 'na',
        description: this.baseline
          ? 'No data date available.'
          : 'Upload a baseline schedule to calculate BEI.',
        failingActivityIds: [],
        population: 0,
      };
    }

    const dd = this.dataDate;
    const shouldHaveFinished = this.baseline.activities.filter(
      (a) => a.finish && a.finish <= dd
    );

    if (shouldHaveFinished.length === 0) {
      return {
        ...base,
        value: 'N/A',
        numeric: null,
        ratio: null,
        status: 'na',
        description: 'The baseline has no activities due to finish by the data date.',
        failingActivityIds: [],
        population: 0,
      };
    }

    const completedCodes = new Set(
      this.schedule.activities.filter((a) => a.isComplete).map((a) => a.code)
    );
    const actuallyFinished = shouldHaveFinished.filter((a) =>
      completedCodes.has(a.code)
    ).length;

    const value = actuallyFinished / shouldHaveFinished.length;
    const behindIds = shouldHaveFinished
      .filter((a) => !completedCodes.has(a.code))
      .map((a) => this.schedule.activities.find((c) => c.code === a.code)?.id)
      .filter((id): id is string => !!id);

    return {
      ...base,
      value: value.toFixed(2),
      numeric: value,
      ratio: value,
      status: value >= 0.95 ? 'pass' : value >= 0.85 ? 'warning' : 'fail',
      description: `${actuallyFinished} of ${shouldHaveFinished.length} activities the baseline expected to be complete by the data date are complete.`,
      failingActivityIds: behindIds,
      population: shouldHaveFinished.length,
    };
  }

  analyze(): DCMAResult {
    const { check: cpliCheck } = this.cpli();

    const checks: DCMACheck[] = [
      this.logic(),
      this.leads(),
      this.lags(),
      this.relationshipTypes(),
      this.hardConstraints(),
      this.highFloat(),
      this.negativeFloat(),
      this.highDuration(),
      this.invalidDates(),
      this.resources(),
      this.missedTasks(),
      this.criticalPathTest(),
      cpliCheck,
      this.bei(),
    ];

    const assessable = checks.filter((c) => c.status !== 'na').length;
    const passed = checks.filter((c) => c.status === 'pass').length;

    return {
      checks,
      passed,
      assessable,
      score: assessable ? passed / assessable : 0,
      incompleteCount: this.incomplete.length,
      dataDate: this.dataDate,
      hasBaseline: !!this.baseline,
      generatedAt: new Date().toISOString(),
    };
  }
}
